import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Formulario } from '../../models/formulario';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AutorizacionesService {
  private apiUrl = environment.apiUrls.autorizaciones;
  private formularioUrl = environment.apiUrls.formulario;
  private fileUrl = environment.apiUrls.file;
  private farmaciaUrl = environment.apiUrls.farmacia;
  private prestadorUrl = environment.apiUrls.prestador;
  private autorizacionUrl = environment.apiUrls.autorizacion;
  private afiliadoUrl = environment.apiUrls.afiliado;

  constructor(private http: HttpClient) { }

  mostrarError(mensaje: string = 'Ocurrió un error al enviar la solicitud. Por favor, inténtalo nuevamente.') {
    Swal.fire({
      title: 'Error',
      text: mensaje,
      icon: 'error',
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#d33'
    });
  }

  // Método unificado que envía formulario + archivos en una sola llamada
  enviarSolicitudCompleta(data: any, archivos: File[], documento: string): Observable<{ idFormulario: number }> {
    /** Validación: verificar que haya archivos antes de enviar */
    if (!archivos || archivos.length === 0) {
      this.mostrarError('Debe adjuntar al menos un archivo para continuar.');
      return throwError(() => new Error('No se adjuntaron archivos'));
    }

    const formData = new FormData();

    // Agregar datos del formulario como JSON
    formData.append('formulario', new Blob([JSON.stringify(data)], { type: 'application/json' }));

    // Agregar documento
    formData.append('documento', documento);

    // Agregar archivos
    for (const archivo of archivos) {
      formData.append('file', archivo);
    }

    return this.http.post<{ idFormulario: number }>(this.formularioUrl, formData).pipe(
      catchError(error => {
        const status = error.status || 'Sin código';
        let errorMessage = 'Ha ocurrido un error. Por favor, inténtelo nuevamente.';

        if (error.error?.error) {
          errorMessage = error.error.mensaje || error.error.error;
        }

        const mensaje = `Error ${status}: ${errorMessage}`;
        this.mostrarError(mensaje);

        return throwError(() => new Error(mensaje));
      })
    );
  }

  // ⚠️ DEPRECATED: Mantener para compatibilidad, pero ya no se usa
  enviarSolicitud(data: any): Observable<{ idFormulario: number }> {
    return this.http.post<{ idFormulario: number }>(this.formularioUrl, data).pipe(
      catchError(error => {
        const status = error.status || 'Sin código';
        let errorMessage = 'Ha ocurrido un error. Por favor, inténtelo nuevamente.';

        const mensaje = `Error ${status}: ${errorMessage}`;
        this.mostrarError(mensaje);

        return throwError(() => new Error(mensaje));
      })
    );
  }

  // ⚠️ DEPRECATED: Ya no se usa porque ahora se envía todo junto
  enviarArchivos(idFormulario: number, archivos: File[], apellido: string, dni: string): Observable<any> {
    const formData = new FormData();

    for (const archivo of archivos) {
      formData.append('file', archivo);
    }

    formData.append('idFormulario', idFormulario.toString());
    formData.append('apellido', apellido);
    formData.append('documento', dni);

    return this.http.post(this.fileUrl, formData, {
      params: { idFormulario },
      responseType: 'text' as 'json'
    }).pipe(
      catchError(error => {
        const status = error.status || 'Sin código';
        console.error('Error al subir archivos:', error);

        this.mostrarError(`Error ${status}: Ha ocurrido un problema al subir los archivos. Por favor, inténtelo nuevamente.`);

        return throwError(() => error);
      })
    );
  }

  obtenerFarmacias(): Observable<any[]> {
    return this.http.get<any[]>(this.farmaciaUrl).pipe(
      map(farmacias => farmacias.map((f, index) => ({
        id: index + 1,
        cuit: f.nroCuit,
        sucursal: f.sucursal,
        nombre: f.nombre,
        localidad: f.localidad
      }))),
      catchError(error => {
        console.error('Error al obtener farmacias', error);
        return of([]);
      })
    );
  }

  obtenerInstituciones(): Observable<any[]> {
    return this.http.get<any[]>(this.prestadorUrl).pipe(
      map(prestadores => prestadores.map(p => ({
        id: p.idPrestadores,
        apellidoRazonSocial: p.apellidoRazonSocial,
        localidad: p.localidad
      }))),
      catchError(error => {
        console.error('Error al obtener instituciones', error);
        return of([]);
      })
    );
  }

  obtenerAutorizaciones(): Observable<any[]> {
    return this.http.get<any[]>(this.autorizacionUrl).pipe(
      map(autorizaciones => autorizaciones.map((a, index) => ({
        id: a.idAutorizar,
        tipoAutorizacion: a.tipoAutorizacion
      }))),
      catchError(error => {
        console.error('Error al obtener autorizaciones', error);
        return of([]);
      })
    );
  }

  buscarAfiliado(tipoDocumento: string, documento: string): Observable<any> {
    return this.http.get<any>(`${this.afiliadoUrl}/${tipoDocumento}/${documento}`).pipe(
      catchError(error => {
        Swal.fire({
          title: 'Información',
          text: 'No encontramos un afiliado activo con ese DNI. Por favor verificá el número ingresado o comunicate con nosotros al 0810 122 2432 o Wp. +54 351 5212170 para ayudarte.',
          icon: 'info',
          confirmButtonColor: '#3085d6'
        });

        return throwError(() => error);
      })
    );
  }

  obtenerFormularioPendiente(tipoDoc: string, nroDoc: number, idFormulario: number): Observable<Formulario> {
    const url = `${this.formularioUrl}/${tipoDoc}/${nroDoc}/${idFormulario}`;

    return this.http.get<Formulario>(url).pipe(
      catchError(error => {
        const status = error.status || 'Sin código';

        let mensaje = 'Ha ocurrido un error al consultar el formulario.';
        if (status === 404) {
          mensaje = 'No cuenta con autorizaciones pendientes.';
        }

        return throwError(() => new Error(mensaje));
      })
    );
  }

  agregarArchivosAlZip(idFormulario: number, archivos: File[], apellido: string, dni: string): Observable<any> {
    const formData = new FormData();

    for (const archivo of archivos) {
      formData.append('file', archivo);
    }

    formData.append('idFormulario', idFormulario.toString());
    formData.append('apellido', apellido);
    formData.append('documento', dni);

    return this.http.post(`${this.fileUrl}/addArchivo`, formData, {
      responseType: 'text' as 'json'
    }).pipe(
      catchError(error => {
        const status = error.status || 'Sin código';

        this.mostrarError(`Error ${status}: No se pudo agregar el archivo. Intenta nuevamente.`);
        return throwError(() => error);
      })
    );
  }

  updateEstadoPendiente(idFormulario: number) {
    return this.http.put<void>(`${this.formularioUrl}/${idFormulario}/updateEstadoPendiente`, null);
  }
}